import { FlexColumn, LabeledInput, useLoading } from '@gorila-shared-ui/components';
import { Checkbox } from 'baseui/checkbox';
import { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../../constants/app';
import { PDF_SETTINGS_DEFAULTS } from '../../../constants/jobs';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { updatePdfSettings } from '../../../services/jobTypesService';
import { updateJobTypeState } from '../../../storage/jobTypesStates';
import { PdfSettings } from '../../../types/app';
import { FormActionsRow } from '../../shared/FormActionsRow';
import StyledModal from '../../shared/StyledModal';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  pdfSettings: PdfSettings;
  jobTypeId: string;
};

export function PdfSettingsModal({ isOpen, onClose, pdfSettings, jobTypeId }: Readonly<Props>) {
  const { css } = useStyles();
  const [form, setForm] = useState<PdfSettings>(PDF_SETTINGS_DEFAULTS);
  const { loading, startLoading, stopLoading } = useLoading();
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const setUpdateForms = useSetRecoilState(updateJobTypeState);

  useEffect(() => {
    if (isOpen) {
      return clearStates();
    }
  }, [isOpen]);

  const clearStates = () => {
    setForm(pdfSettings);
  };

  const onSubmit = async () => {
    startLoading();
    const { error } = await updatePdfSettings(jobTypeId, form);
    if (!error) {
      showPositiveFeedback(FEEDBACK.edited(FEEDBACK_PREFIXES.pdfSettings, pdfSettings.title));
      setUpdateForms('update');
      onCloseModal();
    } else {
      showFailFeedback(error || FEEDBACK.failedCreation(FEEDBACK_PREFIXES.form));
    }
    stopLoading();
  };

  const onChangeInput = (key: keyof PdfSettings, value: any) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onCloseModal = () => {
    onClose();
  };

  return (
    <StyledModal
      isOpen={isOpen}
      closeable
      onClose={onCloseModal}
      size="auto"
      title="Configuración del PDF"
      content={
        <FlexColumn
          classNames={css({
            justifyContent: 'space-between',
            overflow: 'hidden',
            minWidth: '525px',
          })}
        >
          <FlexColumn
            classNames={css({
              overflow: 'auto',
              height: '100%',
            })}
          >
            <LabeledInput
              label="Titulo"
              placeholder="Ingresa el titulo del PDF"
              value={form.title}
              onChange={(value) => onChangeInput('title', value)}
            />
            <Checkbox
              checked={form.allowPDFExport}
              onChange={(e) => onChangeInput('allowPDFExport', e.target.checked)}
            >
              Permitir exportación de PDF
            </Checkbox>
            <Checkbox
              checked={form.showVehicleData}
              onChange={(e) => onChangeInput('showVehicleData', e.target.checked)}
            >
              Mostrar datos del vehículo
            </Checkbox>
            <Checkbox
              checked={form.showClientData}
              onChange={(e) => onChangeInput('showClientData', e.target.checked)}
            >
              Mostrar datos del cliente
            </Checkbox>
            <Checkbox
              checked={form.showTenantLogo}
              onChange={(e) => onChangeInput('showTenantLogo', e.target.checked)}
            >
              Mostrar logotipo de la compañia
            </Checkbox>
            <Checkbox
              checked={form.showTimeline}
              onChange={(e) => onChangeInput('showTimeline', e.target.checked)}
            >
              Mostrar linea del tiempo
            </Checkbox>
            <Checkbox
              checked={form.showPlace}
              onChange={(e) => onChangeInput('showPlace', e.target.checked)}
            >
              Mostrar lugar
            </Checkbox>
          </FlexColumn>
          <FormActionsRow
            canSubmit
            loading={loading}
            onSubmit={onSubmit}
            onCancel={onCloseModal}
            editMode
          />
        </FlexColumn>
      }
    />
  );
}
